import React from 'react';
import { HeartIcon, MessageCircleIcon, SendIcon, BookmarkIcon, MoreHorizontalIcon, StarIcon } from 'lucide-react';
import Avatar from '../shared/Avatar';
interface FeedItemProps {
  post: {
    id: string;
    user: {
      name: string;
      username: string;
      avatar: string;
      verified: boolean;
      role: string;
      sport: string;
    };
    content: {
      type: 'image' | 'video' | 'carousel';
      media: string[];
      caption: string;
      location?: string;
      tags?: string[];
    };
    stats: {
      likes: number;
      comments: number;
      timestamp: string;
    };
    relationship?: {
      type: string;
      entity: string;
    };
  };
}
const FeedItem: React.FC<FeedItemProps> = ({
  post
}) => {
  return <div className="border-b border-gray-100 pb-2 mb-4" data-id="element-334">
      {/* Header */}
      <div className="flex justify-between items-center px-4 py-2" data-id="element-335">
        <div className="flex items-center" data-id="element-336">
          <Avatar src={post.user.avatar} size="md" sport={post.user.sport} data-id="element-337" />
          <div className="ml-3" data-id="element-338">
            <div className="flex items-center" data-id="element-339">
              <span className="font-semibold text-sm" data-id="element-340">
                {post.user.username}
              </span>
              {post.user.verified && <svg className="w-3.5 h-3.5 text-green-500 ml-1" fill="currentColor" viewBox="0 0 20 20" data-id="element-341">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" data-id="element-342"></path>
                </svg>}
            </div>
            <div className="flex text-xs text-gray-500" data-id="element-343">
              <span data-id="element-344">{post.user.role}</span>
              {post.relationship && <>
                  <span className="mx-1" data-id="element-345">•</span>
                  <span data-id="element-346">
                    {post.relationship.type} @ {post.relationship.entity}
                  </span>
                </>}
            </div>
          </div>
        </div>
        <button className="text-gray-500 hover:text-gray-700" data-id="element-347">
          <MoreHorizontalIcon size={20} data-id="element-348" />
        </button>
      </div>
      {/* Media */}
      <div className="relative aspect-square bg-black" data-id="element-349">
        <img src={post.content.media[0]} alt="Post content" className="w-full h-full object-cover" data-id="element-350" />
        {post.content.type === 'video' && <div className="absolute top-2 right-2 bg-black bg-opacity-60 text-white text-xs px-2 py-1 rounded-full" data-id="element-351">
            Video
          </div>}
        {post.content.type === 'carousel' && <div className="absolute bottom-2 right-2" data-id="element-352">
            <div className="flex space-x-1" data-id="element-353">
              {post.content.media.map((_, index) => <div key={index} className={`w-1.5 h-1.5 rounded-full ${index === 0 ? 'bg-white' : 'bg-white bg-opacity-50'}`} data-id="element-354" />)}
            </div>
          </div>}
      </div>
      {/* Actions */}
      <div className="flex justify-between px-4 pt-3" data-id="element-355">
        <div className="flex space-x-4" data-id="element-356">
          <button className="text-gray-800 hover:text-red-500 transition-colors" data-id="element-357">
            <HeartIcon size={24} data-id="element-358" />
          </button>
          <button className="text-gray-800 hover:text-green-500 transition-colors" data-id="element-359">
            <MessageCircleIcon size={24} data-id="element-360" />
          </button>
          <button className="text-gray-800 hover:text-indigo-500 transition-colors" data-id="element-361">
            <SendIcon size={24} data-id="element-362" />
          </button>
        </div>
        <div className="flex space-x-4" data-id="element-363">
          <button className="text-gray-800 hover:text-orange-500 transition-colors" data-id="element-364">
            <StarIcon size={24} data-id="element-365" />
          </button>
          <button className="text-gray-800 hover:text-green-500 transition-colors" data-id="element-366">
            <BookmarkIcon size={24} data-id="element-367" />
          </button>
        </div>
      </div>
      {/* Likes */}
      <div className="px-4 pt-2" data-id="element-368">
        <p className="font-semibold text-sm" data-id="element-369">
          {post.stats.likes.toLocaleString()} likes
        </p>
      </div>
      {/* Caption */}
      <div className="px-4 pt-1" data-id="element-370">
        <p className="text-sm" data-id="element-371">
          <span className="font-semibold" data-id="element-372">{post.user.username}</span>{' '}
          {post.content.caption}
        </p>
      </div>
      {/* Comments */}
      <div className="px-4 pt-1" data-id="element-373">
        <button className="text-gray-500 text-sm hover:text-gray-700" data-id="element-374">
          View all {post.stats.comments} comments
        </button>
      </div>
      {/* Timestamp */}
      <div className="px-4 pt-1" data-id="element-375">
        <p className="text-xs text-gray-500" data-id="element-376">{post.stats.timestamp}</p>
      </div>
      {/* Tags/Mentions */}
      {post.content.tags && post.content.tags.length > 0 && <div className="px-4 pt-2 flex flex-wrap" data-id="element-377">
          {post.content.tags.map((tag, index) => <span key={index} className="text-xs bg-gray-100 text-green-600 rounded-full px-2 py-1 mr-1 mb-1" data-id="element-378">
              {tag}
            </span>)}
        </div>}
    </div>;
};
export default FeedItem;